import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ChevronLeft, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  PRACTICE_KINDS,
  isPracticeKindId,
  type PracticeKindId,
} from '@/practice/registry';

interface Lesson {
  id: string;
  title: string;
  description: string | null;
  course_id: string;
}

interface CourseInfo {
  id: string;
  name: string;
  grade: string | null;
}

interface PracticeItem {
  id: string;
  kind_id: string;
  test_id: string | null;
  worksheet_id: string | null;
  game_id: string | null;
  writing_prompt_id: string | null;
  position: number;
  label?: string;
}

function buildItemUrl(item: PracticeItem, courseGrade: string | null): string | null {
  const fromLesson = `from_lesson=${item.id}`;
  if (item.kind_id === 'question_set' && item.test_id) {
    return `/test/invitation?testId=${item.test_id}&${fromLesson}`;
  }
  if (item.kind_id === 'game' && item.game_id) {
    const gradePath = courseGrade ? `/${courseGrade}` : '';
    return `/games${gradePath}?gameId=${item.game_id}&${fromLesson}`;
  }
  if (item.kind_id === 'worksheet') {
    const gradePath = courseGrade ? `/${courseGrade}` : '';
    return `/worksheets${gradePath}?${fromLesson}`;
  }
  if (item.kind_id === 'writing_prompt') {
    const gradePath = courseGrade ? `/${courseGrade}` : '';
    return `/writing${gradePath}?${fromLesson}`;
  }
  return null;
}

export default function StudentLessonView() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [course, setCourse] = useState<CourseInfo | null>(null);
  const [items, setItems] = useState<PracticeItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!lessonId) return;
    (async () => {
      setLoading(true);
      const { data: l, error } = await supabase
        .from('course_lessons')
        .select('id, title, description, course_id')
        .eq('id', lessonId)
        .is('deleted_at', null)
        .maybeSingle();
      if (error || !l) {
        toast.error('Lesson not found');
        setLoading(false);
        return;
      }
      setLesson(l);

      const { data: c } = await supabase
        .from('courses')
        .select('id, name, grade')
        .eq('id', l.course_id)
        .maybeSingle();
      setCourse(c ?? null);

      const { data: rows } = await supabase
        .from('lesson_practice_items')
        .select('id, kind_id, test_id, worksheet_id, game_id, writing_prompt_id, position')
        .eq('lesson_id', lessonId)
        .order('position', { ascending: true });

      const list = rows ?? [];
      const testIds = list.filter((r: any) => r.test_id).map((r: any) => r.test_id);
      const gameIds = list.filter((r: any) => r.game_id).map((r: any) => r.game_id);
      const [tests, games] = await Promise.all([
        testIds.length
          ? supabase.from('instructor_tests').select('id, title').in('id', testIds)
          : Promise.resolve({ data: [] as any[] }),
        gameIds.length
          ? supabase.from('games').select('id, name').in('id', gameIds)
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const testMap = new Map((tests.data ?? []).map((t: any) => [t.id, t.title]));
      const gameMap = new Map((games.data ?? []).map((g: any) => [g.id, g.name]));

      setItems(
        list.map((r: any) => ({
          ...r,
          label:
            (r.test_id && testMap.get(r.test_id)) ||
            (r.game_id && gameMap.get(r.game_id)) ||
            (r.kind_id === 'writing_prompt' ? 'Writing prompt' : 'Worksheet'),
        })),
      );
      setLoading(false);
    })();
  }, [lessonId]);

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="container max-w-2xl py-10">
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Lesson unavailable.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-3xl py-10">
      <Helmet>
        <title>{lesson.title}</title>
        <meta
          name="description"
          content={lesson.description ?? `Lesson: ${lesson.title}`}
        />
      </Helmet>

      {course && (
        <Link
          to={`/student/courses/${course.id}`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> {course.name}
        </Link>
      )}

      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{lesson.title}</h1>
        {lesson.description && (
          <p className="text-sm text-muted-foreground mt-1">{lesson.description}</p>
        )}
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Practice items</CardTitle>
        </CardHeader>
        <CardContent className="p-0 divide-y">
          {items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No practice items in this lesson yet.
            </div>
          ) : (
            items.map((it, i) => {
              const kind = isPracticeKindId(it.kind_id)
                ? PRACTICE_KINDS[it.kind_id as PracticeKindId]
                : null;
              const Icon = kind?.icon;
              const url = buildItemUrl(it, course?.grade ?? null);
              const inner = (
                <div className="flex items-center gap-3 p-4 w-full">
                  <div className="text-xs text-muted-foreground w-6">{i + 1}</div>
                  {Icon && (
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
                      <Icon className="h-4 w-4" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 text-left">
                    <div className="font-medium truncate">{it.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {kind?.label ?? it.kind_id}
                    </div>
                  </div>
                  {kind?.category && (
                    <Badge variant="outline" className="hidden sm:inline-flex">
                      {kind.category}
                    </Badge>
                  )}
                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                </div>
              );
              return url ? (
                <Link key={it.id} to={url} className="block hover:bg-accent">
                  {inner}
                </Link>
              ) : (
                <div key={it.id} className="opacity-60">{inner}</div>
              );
            })
          )}
        </CardContent>
      </Card>

      {items.length > 0 && (
        <div className="mt-6 flex justify-end">
          <Button asChild variant="ghost">
            <Link to={course ? `/student/courses/${course.id}` : '/dashboard'}>
              Back to course
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
